import { useCallback, useEffect, useRef, useState } from "react";

interface UseScriptOptions {
  src?: string;
  id?: string;
  strategy?: "afterInteractive" | "lazyOnload" | "beforeInteractive" | "worker";
  onLoad?: (e: Event) => void;
  onReady?: () => void;
  onError?: (e: Event) => void;
  defer?: boolean;
  async?: boolean;
  checkOnReadyInterval?: number;
  target?: "head" | "body";
}

type ScriptLoadResult =
  | { status: "ready"; event: Event }
  | { status: "error"; event: Event };

const ScriptCache = new Map<string, Promise<ScriptLoadResult>>();
const LoadCache = new Set();

const useInjectScript = ({
  src,
  id,
  strategy = "afterInteractive",
  onLoad,
  onReady,
  onError,
  defer,
  async,
  checkOnReadyInterval,
  target = "body",
}: UseScriptOptions) => {
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">(
    "idle"
  );
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkAndCallOnReady = useCallback(() => {
    if (
      onReady &&
      window.google &&
      window.google.accounts &&
      window.google.accounts.id
    ) {
      onReady();
    }
  }, [onReady]);

  useEffect(() => {
    if (!src) {
      return;
    }

    const cacheKey = id || src;

    if (cacheKey && LoadCache.has(cacheKey)) {
      setStatus("ready");
      checkAndCallOnReady();
      return;
    }

    const loadScript = () => {
      const cachedScript = ScriptCache.get(src);

      if (cachedScript) {
        cachedScript.then((result) => {
          if (result.status === "ready") {
            if (cacheKey) LoadCache.add(cacheKey);
            setStatus("ready");
            onLoad?.(result.event);
            checkAndCallOnReady();
          } else {
            if (cacheKey) LoadCache.delete(cacheKey);
            setStatus("error");
            onError?.(result.event);
          }
        });
        return;
      }

      const script = document.createElement("script");

      const loadPromise = new Promise<ScriptLoadResult>((resolve) => {
        script.addEventListener("load", function (event) {
          if (cacheKey) LoadCache.add(cacheKey);
          setStatus("ready");
          onLoad?.(event);
          checkAndCallOnReady();
          resolve({ status: "ready", event });
        });

        script.addEventListener("error", function (event) {
          ScriptCache.delete(src);
          if (cacheKey) LoadCache.delete(cacheKey);
          setStatus("error");
          onError?.(event);
          resolve({ status: "error", event });
        });
      });

      script.src = src;
      if (defer) script.defer = true;
      if (async) script.async = true;

      ScriptCache.set(src, loadPromise);

      // cSpell:ignore nscript
      script.setAttribute("data-nscript", strategy);

      if (target === "head") {
        document.head.appendChild(script);
      } else {
        document.body.appendChild(script);
      }
    };

    if (strategy === "afterInteractive") {
      loadScript();
    } else if (strategy === "lazyOnload") {
      window.addEventListener("load", () => {
        requestIdleCallback(() => loadScript());
      });
    } else if (strategy === "beforeInteractive" || strategy === "worker") {
      loadScript();
    }

    // Set up interval to check and call onReady only if checkOnReadyInterval is defined and greater than 0
    if (checkOnReadyInterval && checkOnReadyInterval > 0) {
      intervalRef.current = setInterval(
        checkAndCallOnReady,
        checkOnReadyInterval
      );
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [
    src,
    id,
    strategy,
    onLoad,
    onReady,
    onError,
    defer,
    async,
    checkAndCallOnReady,
    checkOnReadyInterval,
    target,
  ]);

  return status;
};

export default useInjectScript;
